import { KNOWN_PLACEHOLDER_KEYS, PlaceholderKey } from '../catalog';
import { replaceKnownPlaceholders } from '../utils/placeholderHtml';

export interface AdvisorTemplateValues {
  agent_id: number;
  name: string;
  surname: string;
  address: string;
  zip: string;
  city: string;
  country_id: number;
  branch_office_id: number;
  country?: { id: number; title: string } | null;
  branch_office?: { id: number; title: string } | null;
  email_corp: string;
  email_private: string;
  business_owner: string;
  phone?: string | null;
  mobile?: string | null;
  fax?: string | null;
  birthday?: string | null;
  is_male?: number | null;
  status_id?: number | null;
  rank_id?: number | null;
  level_id?: number | null;
  contract_type_id?: number | null;
  address2?: string | null;
  zip2?: string | null;
  city2?: string | null;
  country2_id?: number | null;
  iban?: string | null;
  bic?: string | null;
  bank_name?: string | null;
  tax_nr?: string | null;
  ust_nr?: string | null;
  start_date?: string | null;
  agent_account_nr?: number | null;
  myjack_login?: string | null;
  mandateReference?: string | null;
}

export interface AdvisorPlaceholderContext {
  officeTitle: string;
  countryTitle: string;
  statusTitle: string;
  rankTitle: string;
  levelTitle: string;
  contractTypeName: string;
  country2Title: string;
}

/**
 * Kalendertag (Geburtstag, Startdatum) als TT.MM.JJJJ. Die Tageskomponenten werden direkt gelesen,
 * ohne `Date`, damit keine Zeitzone den Tag verschiebt (HOTSPOTS „Date-only-Feld“).
 */
function formatCalendarDay(day: string | null | undefined): string {
  if (!day) return '';
  const match = day.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : day;
}

/** RITA-1514: Anrede aus dem Geschlecht des Beraters; ohne gepflegtes Geschlecht bleibt die Stelle leer. */
function formatSalutation(isMale: number | null | undefined): string {
  if (isMale === 1) return 'Herr';
  if (isMale === 0) return 'Frau';
  return '';
}

function formatGender(isMale: number | null | undefined): string {
  if (isMale === 1) return 'Männlich';
  if (isMale === 0) return 'Weiblich';
  return '';
}

/**
 * Werte aller Platzhalter des gemeinsamen Katalogs für einen Berater. Gemeinsamer Baustein der
 * Systemzugangs-Mails und der Vertragserstellung (RITA-1543); dort ergänzt
 * `features/contract-creation/services/vertragPlatzhalterwerte.ts` die vertragseigenen Schlüssel.
 */
export function advisorPlaceholderValues(
  values: AdvisorTemplateValues,
  context: AdvisorPlaceholderContext,
): Record<PlaceholderKey, string> {
  return {
    ANREDE: formatSalutation(values.is_male),
    VORNAME: values.name || '',
    NACHNAME: values.surname || '',
    GEBURTSTAG: formatCalendarDay(values.birthday),
    GESCHLECHT: formatGender(values.is_male),
    STATUS: context.statusTitle,
    TELEFON: values.phone || '',
    MOBILTELEFON: values.mobile || '',
    FAX: values.fax || '',
    GESCHAEFTSEMAIL: values.email_corp || '',
    PRIVATEEMAIL: values.email_private || '',
    STRASSE: values.address || '',
    PLZ: values.zip || '',
    ORT: values.city || '',
    LAND: context.countryTitle,
    RA_STRASSE: values.address2 || '',
    RA_PLZ: values.zip2 || '',
    RA_ORT: values.city2 || '',
    RA_LAND: context.country2Title,
    RANG: context.rankTitle,
    LEVEL: context.levelTitle,
    FIRMA: context.officeTitle,
    VERTRAGSART: context.contractTypeName,
    BERATERNUMMER: String(values.agent_id || ''),
    MYJACK_UNIT: '',
    MYJACK_LOGIN: values.myjack_login || '',
    MANDATSREFERENZ: values.mandateReference ?? '',
    // RITA-1514: wörtlich aus dem Feld „Abweichender Vertragsinhaber“; leer bleibt leer,
    // kein automatischer Ersatz durch Anrede und Namen (Entscheidung der Auftraggeberin, 17.09.2026).
    VERTRAGSINHABER: values.business_owner ?? '',
    KREDITORENNUMMER: String(values.agent_account_nr || ''),
    STEUERNUMMER: values.tax_nr || '',
    // RITA-1532: wörtlich aus „Umsatzsteuer ID“ der Beratermaske; leer bleibt leer.
    USTID: values.ust_nr || '',
    IBAN: values.iban || '',
    BANKNAME: values.bank_name || '',
    BIC: values.bic || '',
    // RITA-1532: wie in der Beratermaske als TT.MM.JJJJ (vorher ISO JJJJ-MM-TT).
    STARTDATUM: formatCalendarDay(values.start_date),
    TEAMLEITER: '',
  };
}

export function renderAdvisorTemplate(
  template: string,
  values: AdvisorTemplateValues,
  context: AdvisorPlaceholderContext,
): string {
  return replaceKnownPlaceholders(template, advisorPlaceholderValues(values, context), KNOWN_PLACEHOLDER_KEYS);
}
